import React, { useEffect, useRef } from 'react';
import { StyleSheet, TouchableOpacity, Animated } from 'react-native';
import { Text } from 'trpc-react-native-ui';
import type { Region } from 'digicel-kaizen-library';

import theme from '../../../../theme-config';
import { Icon } from '../../../../components/common';

type Props = {
  text: string,
  iconName: string,
  region: Region,
  isSelected?: boolean,
  onPress?: (region: Region) => void,
  testID: string,
  iconSize?: number,
  fontSize?: number,
};

const AnimatedText = Animated.createAnimatedComponent(Text);
const AnimatedIcon = Animated.createAnimatedComponent(Icon);

const RegionItem = ({ text, iconName, region, isSelected, onPress, testID, iconSize = 100, fontSize = 15 }: Props) => {
  const animationValue = useRef(new Animated.Value(isSelected ? 1 : 0)).current;

  useEffect(() => {
    Animated.spring(animationValue, {
      toValue: isSelected ? 1 : 0,
      useNativeDriver: false,
      friction: 8,
      tension: 40,
    }).start();
  }, [isSelected, animationValue]);

  if (!region) {
    return null;
  }

  const scale = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.15],
  });

  const color = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [theme.gray.light, theme.secondaryColor],
  });

  const textColor = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [theme.gray.normal, theme.secondaryColor],
  });

  const handlePress = () => {
    if (onPress) {
      onPress(region);
    }
  };

  return (
    <Animated.View style={[styles.container, { transform: [{ scale }] }]}>
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={handlePress}
        testID={testID}
        style={styles.touchable}
      >
        <AnimatedIcon
          name={iconName}
          size={iconSize}
          color={color}
          style={styles.icon}
        />
        <AnimatedText style={[styles.text, { color: textColor, fontSize }]}>
          {text}
        </AnimatedText>
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: 20,
  },
  touchable: {
    alignItems: 'center',
  },
  icon: {
    marginBottom: 10,
  },
  text: {
    textAlign: 'center',
    fontFamily: 'Gotham',
  },
});

export default RegionItem;
